import { ManfrediteCvApplication } from '@asgard/common-dto';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts/useToasts';
import { PublicManfrediteJobApplicationResume } from 'interfaces/manfredite.interface';
import { PublicManfredite } from 'interfaces/public-manfredite.interface';
import { useContext, useReducer } from 'react';
import ManfrediteJobApplicationService from 'services/manfredite-job-application.service';
import { getErrorMessage } from 'utils/errors';
import CVApplicationTermsAcceptedComponent from './CVApplicationTermsAcceptedComponent';
import CVApplicationTermsNotAcceptedComponent from './CVApplicationTermsNotAcceptedComponent';
import CVApplicationViewReducer, {
  CVApplicationViewActionType
} from './CVApplicationViewComponent.reducer';

export type CVApplicationViewComponentProps = {
  termsAndConditionsAccepted: boolean;
  slug: string;
  jobApplicationResume: PublicManfrediteJobApplicationResume;
  publicManfredite?: PublicManfredite;
  cvApplication?: ManfrediteCvApplication;
  previewMode?: boolean;
};

const CVApplicationViewComponent: React.FC<CVApplicationViewComponentProps> = (
  props
) => {
  const { t } = useI18n('cv-applications-view');
  const { addErrorToast } = useToasts();
  const { user } = useContext(LoggedUserContext);

  const [state, dispatch] = useReducer(CVApplicationViewReducer, {
    termsAndConditionsAccepted: props.termsAndConditionsAccepted,
    jobApplicationResume: props.jobApplicationResume,
    publicManfredite: props.publicManfredite,
    cvApplication: props.cvApplication
  });

  const handleTermsAcceptedClick = async (): Promise<void> => {
    if (props.previewMode) {
      dispatch({
        type: CVApplicationViewActionType.ACCEPT_TERMS,
        payload: {
          data: {
            jobApplicationResume: props.jobApplicationResume,
            jobApplication: props.cvApplication,
            publicManfredite: props.publicManfredite
          }
        }
      });
      return;
    }

    try {
      await ManfrediteJobApplicationService.acceptTermsAndConditions(
        props.slug
      );

      const jobApp = await ManfrediteJobApplicationService.getBySlug(
        props.slug,
        user?.id
      );

      if (!jobApp.publicManfredite) {
        throw new Error(
          'Could not accept terms and conditions for this cv application'
        );
      }

      dispatch({
        type: CVApplicationViewActionType.ACCEPT_TERMS,
        payload: {
          data: jobApp
        }
      });
    } catch (ex) {
      console.error(
        `Could not accept terms and conditions for job app ${props.slug}, by Manfredite: ${user?.id}. Exception: ${ex}}`
      );

      addErrorToast(
        t('cvApplicationsView.modalTerms.messages.error.acceptTerms', {
          error: getErrorMessage(ex)
        })
      );
    }
  };

  return (
    <>
      {!state.termsAndConditionsAccepted && (
        <CVApplicationTermsNotAcceptedComponent
          jobApplicationResume={state.jobApplicationResume}
          onAcceptTermsClick={handleTermsAcceptedClick}
          viewCvType={props.jobApplicationResume.view}
        />
      )}

      {state.termsAndConditionsAccepted &&
        state.publicManfredite &&
        state.cvApplication && (
          <CVApplicationTermsAcceptedComponent
            publicManfredite={state.publicManfredite}
            jobApplicationResume={state.jobApplicationResume}
            cvApplication={state.cvApplication}
            previewMode={props.previewMode}
          />
        )}
    </>
  );
};

export default CVApplicationViewComponent;
